import React, { useState, useEffect, useContext, useMemo, useRef, Fragment } from 'react';
import { useHistory } from 'react-router-dom';
import { Dialog, Transition } from '@headlessui/react';
import { useQueryClient } from '@tanstack/react-query';
import {
  Command,
  CommandInput,
  CommandList,
  CommandEmpty,
  CommandGroup,
  CommandItem,
  CommandShortcut,
  Icon,
  useSidebarNav
} from '@aidkitorg/component-library';
import { IconName } from '@aidkitorg/component-library/dist/types/src/Icon';
import InterfaceContext, { LoggedInConfigurationContext, SupportedLanguage, UserInfoContext } from '../Context';
import { useLocalizedStrings, langToWord } from '../Localization';
import { useTheme } from '../Contexts/ThemeContext';
import { checkPageAccess } from '@aidkitorg/types/lib/admin-panel/permissions';
import { DashboardWithId } from '@aidkitorg/types/lib/survey';
import { usePost } from '../API';
import { flattenNavItemsForCommandPalette, getNavItems } from './navigationConfig';
import { useOrganizationsEnabled } from './shared/hooks/useOrganizationsEnabled';
import { mapDashboardsToPaletteCommands } from './commandPaletteDashboards';
import {
  distroReportsQueryKey,
  mapReportsToPaletteCommands,
  mergeFetchedReportsForPalette,
} from './commandPaletteReports';
import type { Report } from '../Contexts/ReportsContext';

type CommandAction = {
  id: string;
  label: string;
  description?: string;
  href?: string;
  iconName?: IconName;
  shortcut?: string;
  category: 'navigation' | 'actions' | 'recent' | 'dashboards' | 'reports';
  searchTerms?: string[];
  isExternal?: boolean;
  priority?: number; // Higher priority = shown by default
};

/** Must match DashboardsContext cache key so a warm dashboards page avoids a duplicate fetch. */
const DISTRO_DASHBOARDS_QUERY_KEY = ['distro-dashboards'];

const ENTITY_SEARCH_CATEGORIES = new Set(['dashboards', 'reports']);

interface AdminCommandPaletteProps {
  isOpen: boolean;
  onClose: () => void;
}

export const AdminCommandPalette: React.FC<AdminCommandPaletteProps> = ({
  isOpen,
  onClose
}) => {
  const [search, setSearch] = useState('');
  const [dashboardCommands, setDashboardCommands] = useState<CommandAction[]>([]);
  const [reportCommands, setReportCommands] = useState<CommandAction[]>([]);
  const history = useHistory();
  const L = useLocalizedStrings();
  const interfaceContext = useContext(InterfaceContext);
  const userInfo = useContext(UserInfoContext);
  const loggedInConfig = useContext(LoggedInConfigurationContext);
  const { enabled: orgsEnabled } = useOrganizationsEnabled();
  const { toggleSidebarNav } = useSidebarNav();
  const { languages } = useTheme();
  const queryClient = useQueryClient();
  const listDashboardsPost = usePost('/distro/dashboard/list', { suppressErrorDisplay: true });
  const listDashboardsPostRef = useRef(listDashboardsPost);
  listDashboardsPostRef.current = listDashboardsPost;
  const listReportsPost = usePost('/distro/report/list', { suppressErrorDisplay: true });
  const listReportsPostRef = useRef(listReportsPost);
  listReportsPostRef.current = listReportsPost;
  const getAdminPanelReports = usePost('/api/admin_panel/reports', { suppressErrorDisplay: true });
  const getAdminPanelReportsRef = useRef(getAdminPanelReports);
  getAdminPanelReportsRef.current = getAdminPanelReports;

  const canViewDashboards = checkPageAccess(userInfo.tags, 'dashboards', 'view');
  const canViewReviewQueues = checkPageAccess(userInfo.tags, 'review-queues', 'view');
  const canViewReports = checkPageAccess(userInfo.tags, 'reports', 'view');

  // Simple fuzzy search function
  const fuzzyMatch = (text: string, query: string): { isMatch: boolean; score: number } => {
    if (!text || !query) return { isMatch: false, score: 0 };
    
    text = text.toLowerCase();
    query = query.toLowerCase();
    
    // Exact match gets highest score
    if (text.includes(query)) {
      return { isMatch: true, score: 100 };
    }
    
    // Check if all characters in query exist in text (in order)
    let textIndex = 0;
    let matchedChars = 0;
    
    for (let i = 0; i < query.length; i++) {
      const char = query[i];
      const foundIndex = text.indexOf(char, textIndex);
      
      if (foundIndex !== -1) {
        matchedChars++;
        textIndex = foundIndex + 1;
      }
    }
    
    // Calculate fuzzy score based on character matches and text length
    const matchRatio = matchedChars / query.length;
    const lengthPenalty = Math.max(0, 1 - (text.length - query.length) / text.length);
    const score = matchRatio * 80 * (0.5 + lengthPenalty * 0.5);
    
    // Consider it a match if more than 70% of characters match
    return { isMatch: matchRatio > 0.7, score };
  };

  useEffect(() => {
    if (!isOpen || (!canViewDashboards && !canViewReviewQueues)) {
      return;
    }

    let cancelled = false;

    const loadDashboards = async () => {
      try {
        let rawDashboards = queryClient.getQueryData<DashboardWithId[]>(DISTRO_DASHBOARDS_QUERY_KEY);
        if (!rawDashboards) {
          // usePost returns { error } without throwing when suppressErrorDisplay is set.
          // Only cache real arrays — coercing errors to [] poisons the shared key
          // (empty arrays are truthy, so later readers never refetch).
          const result = await listDashboardsPostRef.current({});
          if (!Array.isArray(result)) {
            throw new Error('Failed to list dashboards');
          }
          rawDashboards = result;
          queryClient.setQueryData(DISTRO_DASHBOARDS_QUERY_KEY, rawDashboards);
        }

        if (cancelled) return;

        setDashboardCommands(
          mapDashboardsToPaletteCommands(rawDashboards, {
            lang: interfaceContext.lang,
            canViewDashboards,
            canViewReviewQueues,
          })
        );
      } catch {
        if (!cancelled) {
          setDashboardCommands([]);
        }
      }
    };

    void loadDashboards();

    return () => {
      cancelled = true;
    };
  }, [
    isOpen,
    canViewDashboards,
    canViewReviewQueues,
    queryClient,
    interfaceContext.lang,
  ]);

  useEffect(() => {
    if (!isOpen || !canViewReports) {
      return;
    }

    let cancelled = false;

    const loadReports = async () => {
      try {
        const reportsQueryKey = distroReportsQueryKey(interfaceContext.lang);
        let mergedReports = queryClient.getQueryData<Report[]>(reportsQueryKey);
        if (!mergedReports) {
          // usePost returns { error } without throwing when suppressErrorDisplay is set.
          // Only cache when both sources succeed — coercing failures to [] poisons the
          // shared key (empty arrays are truthy, so ReportsContext never refetches).
          const [distroResult, dbResult] = await Promise.all([
            listReportsPostRef.current({}).catch(() => null),
            getAdminPanelReportsRef.current({}).catch(() => null),
          ]);

          const { reports, cacheable } = mergeFetchedReportsForPalette(
            distroResult,
            dbResult,
            interfaceContext.lang
          );
          if (!cacheable && reports.length === 0) {
            throw new Error('Failed to list reports');
          }
          mergedReports = reports;
          if (cacheable) {
            queryClient.setQueryData(reportsQueryKey, mergedReports);
          }
        }

        if (cancelled) return;

        setReportCommands(mapReportsToPaletteCommands(mergedReports));
      } catch {
        if (!cancelled) {
          setReportCommands([]);
        }
      }
    };

    void loadReports();

    return () => {
      cancelled = true;
    };
  }, [isOpen, canViewReports, queryClient, interfaceContext.lang]);

  // All available commands - registered from navigation + additional actions
  const allCommands: CommandAction[] = useMemo(() => {
    const navItems = getNavItems(L, userInfo.tags, loggedInConfig, { organizationsEnabled: orgsEnabled });
    const navCommands: CommandAction[] = flattenNavItemsForCommandPalette(
      navItems,
      interfaceContext.lang,
      L.admin_panel.command_palette.search_terms.navigation
    ).map((cmd) => ({
      ...cmd,
      category: 'navigation' as const,
    }));

    return [
      ...navCommands,
      // Additional actions
      {
        id: 'action-create-report',
        label: L.admin_panel.command_palette.actions.create_report.label,
        description: L.admin_panel.command_palette.actions.create_report.description,
        href: '/admin-panel/reports/new',
        iconName: 'PlusIcon',
        category: 'actions',
        priority: 8,
        searchTerms: L.admin_panel.command_palette.search_terms.actions.create_report.split(',')
      },
      {
        id: 'action-create-dashboard',
        label: L.admin_panel.command_palette.actions.create_dashboard.label,
        description: L.admin_panel.command_palette.actions.create_dashboard.description,
        href: '/admin-panel/dashboards/new',
        iconName: 'PlusIcon',
        category: 'actions',
        priority: 8,
        searchTerms: L.admin_panel.command_palette.search_terms.actions.create_dashboard.split(',')
      },
      {
        id: 'action-manage-reminders',
        label: L.admin_panel.command_palette.actions.manage_reminders.label,
        description: L.admin_panel.command_palette.actions.manage_reminders.description,
        href: '/admin-panel/balances?action=manage-reminders',
        iconName: 'CalendarIcon',
        category: 'actions',
        priority: 4,
        searchTerms: L.admin_panel.command_palette.search_terms.actions.manage_reminders.split(',')
      },
      {
        id: 'action-toggle-sidebar',
        label: L.admin_panel.command_palette.actions.toggle_sidebar.label,
        description: L.admin_panel.command_palette.actions.toggle_sidebar.description,
        iconName: 'Bars3Icon',
        category: 'actions',
        priority: 3,
        searchTerms: L.admin_panel.command_palette.search_terms.actions.toggle_sidebar.split(',')
      },
      {
        id: 'action-logout',
        label: L.menu.logout,
        description: L.admin_panel.command_palette.actions.logout.description,
        iconName: 'ArrowRightOnRectangleIcon',
        category: 'actions',
        priority: 2,
        searchTerms: L.admin_panel.command_palette.search_terms.actions.logout.split(',')
      },
      // Dynamic language switching actions
      ...languages.map((lang) => ({
        id: `action-language-${lang}`,
        label: `${L.admin_panel.command_palette.actions.change_language.label}: ${langToWord(lang)}`,
        description: `${L.admin_panel.command_palette.actions.change_language.description} - ${langToWord(lang)}`,
        iconName: 'LanguageIcon' as IconName,
        category: 'actions' as const,
        priority: 0, // Low priority so they appear at bottom
        searchTerms: [
          ...L.admin_panel.command_palette.search_terms.actions.change_language.split(','),
          langToWord(lang)?.toLowerCase() || '',
          lang
        ]
      })),
      ...dashboardCommands,
      ...reportCommands,
    ];
  }, [L, languages, userInfo.tags, loggedInConfig, orgsEnabled, interfaceContext.lang, dashboardCommands, reportCommands]);

  // Filter and sort commands based on search
  const { filteredCommands, commandScores } = useMemo(() => {
    const searchLower = search.toLowerCase().trim();
    
    if (!searchLower) {
      // Show only high priority items when no search; omit entity results to avoid flooding
      const commands = allCommands
        .filter(cmd => !ENTITY_SEARCH_CATEGORIES.has(cmd.category) && (cmd.priority || 0) >= 5)
        .sort((a, b) => (b.priority || 0) - (a.priority || 0));
      return { filteredCommands: commands, commandScores: new Map() };
    }
    
    // When searching, show all matching commands with fuzzy search
    const commandsWithScores = allCommands
      .map(cmd => {
        const labelMatch = cmd.label?.toLowerCase().includes(searchLower);
        const searchTermsMatch = cmd.searchTerms?.some(term => 
          term.toLowerCase().includes(searchLower)
        );
        
        // Use fuzzy search for descriptions
        const descriptionFuzzy = cmd.description 
          ? fuzzyMatch(cmd.description, searchLower) 
          : { isMatch: false, score: 0 };
        
        const isMatch = labelMatch || searchTermsMatch || descriptionFuzzy.isMatch;
        
        // Calculate combined score for sorting
        let score = 0;
        if (labelMatch) {
          // Prioritize label matches
          score += cmd.label.toLowerCase().startsWith(searchLower) ? 100 : 80;
        }
        if (searchTermsMatch) {
          score += 60;
        }
        if (descriptionFuzzy.isMatch) {
          score += descriptionFuzzy.score * 0.5; // Weight description matches lower
        }
        
        // Add priority bonus
        score += (cmd.priority || 0) * 2;
        
        return { cmd, score, isMatch };
      })
      .filter(item => item.isMatch)
      .sort((a, b) => b.score - a.score);
    
    const commands = commandsWithScores.map(item => item.cmd);
    const scores = new Map(commandsWithScores.map(item => [item.cmd.id, item.score]));
    
    return { filteredCommands: commands, commandScores: scores };
  }, [search, allCommands]);

  // Group commands by category
  const groupedCommands = useMemo(() => {
    const groups: Record<string, CommandAction[]> = {};
    
    filteredCommands.forEach(cmd => {
      if (!groups[cmd.category]) {
        groups[cmd.category] = [];
      }
      groups[cmd.category].push(cmd);
    });
    
    return groups;
  }, [filteredCommands]);

  // Handle command execution
  const executeCommand = (command: CommandAction) => {
    onClose();
    
    switch (command.id) {
      case 'action-logout':
        window.location.assign('/logout');
        break;
      
      case 'action-toggle-sidebar':
        toggleSidebarNav();
        break;
      
      default:
        // Handle language switching actions
        if (command.id.startsWith('action-language-')) {
          const langCode = command.id.replace('action-language-', '');
          interfaceContext.setLanguage(langCode as SupportedLanguage);
          break;
        }
        
        // Handle regular navigation
        if (command.href) {
          if (command.isExternal) {
            window.open(command.href, '_blank', 'noopener,noreferrer');
          } else {
            history.push(command.href);
          }
        }
        break;
    }
  };

  const getCategoryLabel = (category: string) => {
    switch (category) {
      case 'navigation':
        return L.admin_panel.command_palette.categories.navigation;
      case 'actions':
        return L.admin_panel.command_palette.categories.actions;
      case 'recent':
        return L.admin_panel.command_palette.categories.recent;
      case 'dashboards':
        return L.admin_panel.command_palette.categories.dashboards || 'Dashboards';
      case 'reports':
        return L.admin_panel.command_palette.categories.reports || 'Reports';
      default:
        return category;
    }
  };

  const renderCommandGroups = () => {
    // When searching, order categories by their highest-scoring result
    // When not searching, use default order
    let categoryOrder = ['navigation', 'dashboards', 'reports', 'actions', 'recent'];
    
    if (search) {
      // Calculate max score for each category
      const categoryScores = Object.entries(groupedCommands).map(([category, commands]) => {
        if (!commands || commands.length === 0) return { category, maxScore: -1 };
        
        // Find the highest score in this category using commandScores map
        const maxScore = Math.max(...commands.map(cmd => commandScores.get(cmd.id) || 0));
        
        return { category, maxScore };
      });
      
      // Sort categories by max score (highest first)
      categoryOrder = categoryScores
        .filter(cs => cs.maxScore >= 0)
        .sort((a, b) => b.maxScore - a.maxScore)
        .map(cs => cs.category);
    }
    
    return categoryOrder.map(category => {
      const commands = groupedCommands[category];
      if (!commands || commands.length === 0) return null;
      
      return (
        <CommandGroup key={category} heading={getCategoryLabel(category)}>
          {commands.map(command => (
            <CommandItem
              key={command.id}
              value={`${command.label} ${command.description || ''} ${command.searchTerms?.join(' ') || ''}`}
              onSelect={() => executeCommand(command)}
              className="flex items-start gap-2 px-2 py-1.5"
            >
              {command.iconName && (
                <Icon 
                  name={command.iconName} 
                  className="h-4 w-4 shrink-0 text-gray-600 mt-[2px]" 
                  role="decorative"
                  type="solid"
                />
              )}
              <div className="flex flex-col flex-1 min-w-0">
                <span className="text-sm font-medium truncate">
                  {command.label}
                </span>
                {search && command.description && (
                  <span className="text-xs text-gray-600 truncate">
                    {command.description}
                  </span>
                )}
              </div>
              {command.shortcut && (
                <CommandShortcut>{command.shortcut}</CommandShortcut>
              )}
            </CommandItem>
          ))}
        </CommandGroup>
      );
    });
  };

  return (
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={onClose}>
        <div className="fixed inset-0 flex items-start justify-center pt-[20vh]">
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-200"
            enterFrom="opacity-0 scale-95"
            enterTo="opacity-100 scale-100"
            leave="ease-in duration-100"
            leaveFrom="opacity-100 scale-100"
            leaveTo="opacity-0 scale-95"
            afterLeave={() => setSearch('')}
          >
            <Dialog.Panel className="bg-white rounded-lg shadow-2xl ring-1 ring-black/10 overflow-hidden w-full max-w-2xl mx-4">
              <Dialog.Title className="sr-only">
                {L.admin_panel.command_palette.title}
              </Dialog.Title>
              <Command>
                <CommandInput 
                  placeholder={L.admin_panel.command_palette.placeholder} 
                  className="h-12 text-base border-none focus:ring-0"
                  value={search}
                  onValueChange={setSearch}
                />
                <CommandList className="max-h-96">
                  <CommandEmpty>
                    <div className="py-6 text-center text-sm text-gray-700">
                      <Icon name="MagnifyingGlassIcon" className="mx-auto h-6 w-6 mb-2 opacity-50" type="solid" />
                      {L.admin_panel.command_palette.no_commands_found}
                    </div>
                  </CommandEmpty>
                  {renderCommandGroups()}
                </CommandList>
              </Command>
            </Dialog.Panel>
          </Transition.Child>
        </div>
      </Dialog>
    </Transition>
  );
};
